import StripePayment, { StripePaymentOptions } from './stripe-payment'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Lock } from 'react-feather'
import poweredByStripe from '../assets/powered-by-stripe.svg'
import { cn } from '../utils/cn'
import { InteracPayment } from './interac-payment'
import { ExchangeRates } from '../services/bookings'

type PaymentOptions = StripePaymentOptions & {
  countryCode: string
  bookingNumber: string
  exchangeRates?: ExchangeRates
  onComplete?: () => void
}

type CanadianPaymentMethod = 'transfer' | 'stripe'

function TabButton<T extends String> ({ currentValue, value, onClick, title, description, badge }: {
  currentValue?: T
  title: string
  description: string
  value: T
  onClick: (newValue: T) => void
  badge?: string
}): JSX.Element {
  return (
    <div
      className={cn('relative p-4 rounded-md cursor-pointer flex items-center', currentValue === value ? 'ring-2 ring-primary' : 'ring ring-gray-500')}
      onClick={() => onClick(value)}
    >
      {badge != null && (
        <span className='absolute -top-2 right-6 bg-brand-well text-primary text-xs font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full'>
          {badge}
        </span>
      )}
      <div className='grow'>
        <div className='font-bold text-primary'>{title}</div>
        <div className='text-gray-500'>{description}</div>
      </div>
      <div className='px-4'>
        <div className='border rounded-full size-5 border-primary flex justify-center items-center'>
          {currentValue === value && (
            <div className='bg-primary size-3 rounded-full' />
          )}
        </div>
      </div>
    </div>
  )
}

export function CanadianPayment ({
  countryCode,
  bookingNumber,
  amount,
  exchangeRates,
  onComplete,
  ...rest
}: PaymentOptions): JSX.Element {
  const { t } = useTranslation()
  const [paymentMethod, setPaymentMethod] = useState<CanadianPaymentMethod>('transfer')

  if (exchangeRates == null || !('CAD' in exchangeRates)) {
    throw new Error('Cannot convert currency')
  }

  return (
    <div>
      <div className='grid lg:grid-cols-2 gap-4 mb-8'>
        <TabButton
          currentValue={paymentMethod}
          value={'transfer' as const} onClick={value => setPaymentMethod(value)}
          title={t('payment:methods.interac.title')}
          description={t('payment:methods.interac.description')} badge={t('payment:methods.interac.badge')}
        />
        <TabButton
          currentValue={paymentMethod}
          value={'stripe' as const} onClick={value => setPaymentMethod(value)}
          title={t('payment:methods.card.title')}
          description={t('payment:methods.card.description')}
        />
      </div>
      <div className={paymentMethod !== 'transfer' ? 'hidden' : ''}>
        <div className='my-4'>
          <SafeSecureBanner />
        </div>
        <InteracPayment
          onComplete={onComplete} bookingNumber={bookingNumber} amount={amount ?? 0}
          exchangeRate={exchangeRates.CAD}
        />
      </div>
      <div className={paymentMethod !== 'stripe' ? 'hidden' : ''}>
        <div className='mb-4'>
          <SafeSecureBanner stripeLogo />
        </div>
        <StripePayment amount={amount} {...rest} />
      </div>
    </div>
  )
}

export function Payment ({ countryCode, market, exchangeRates, ...rest }: PaymentOptions): JSX.Element {
  if (market === 'north_america' && countryCode === 'CA' && exchangeRates != null && 'CAD' in exchangeRates) {
    return <CanadianPayment market={market} countryCode={countryCode} exchangeRates={exchangeRates} {...rest} />
  }
  return (
    <StripePayment market={market} {...rest} />
  )
}

export function SafeSecureBanner ({ stripeLogo = false }: { stripeLogo?: boolean }): JSX.Element {
  const { t } = useTranslation()
  return (
    <div className='flex gap-2 items-center text-[#0a2540]'>
      <Lock size={24} />
      <span>
        <Trans i18nKey='payment:safe_secure_checkout'>
          Guaranteed <strong>safe &amp; secure</strong> checkout
        </Trans>
      </span>
      {stripeLogo && (<img alt={t('payment:powered_by_stripe')} src={poweredByStripe} width={150} />)}
    </div>
  )
}
